/** Pure label/summary helpers for the audit HistorySection (UX C4, spec 3.11).
 *  Split out of HistorySection.tsx so this module exports no React component —
 *  co-locating pure helpers with a component export trips
 *  react-refresh/only-export-components (see Task 7.5's attentionCards.ts
 *  precedent). */
import type { EntityHistoryEntry } from '../../types/topology.ts'

const ACTION_LABELS: Record<string, string> = {
  verify: 'Verification changed',
  assign: 'Assigned',
  reassign: 'Reassigned',
  accept_gis: 'GIS value accepted',
  auto_downgrade: 'Verification auto-downgraded',
  confirm_gis_value: 'GIS value confirmed',
  restore_gis_value: 'GIS value restored',
  auto_retire: 'Auto-retired',
}

/** Friendly label for known actions; generic (underscores → spaces) otherwise. */
export function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action.replace(/_/g, ' ')
}

/** Plain-English names for the FK columns the reassign paths record. Anything
 *  else falls back to the raw field name — the ledger's field vocabulary is as
 *  open as its action vocabulary. */
const FIELD_LABELS: Record<string, string> = {
  transformer_id: 'transformer',
  feeder_id: 'feeder',
}

/** What this row actually changed, or null when the row records no field
 *  change. "Reassigned · by godfreytj" alone tells an operator nothing — the
 *  ledger's field_name/old_value/new_value are what make the entry useful. */
export function changeSummary(entry: EntityHistoryEntry): string | null {
  const field = entry.field_name
  if (!field) return null
  const from = entry.old_value
  const to = entry.new_value
  if (from == null && to == null) return null
  const label = FIELD_LABELS[field]
  if (label) {
    if (from != null && to != null) return `moved from ${label} ${from} to ${to}`
    if (to != null) return `set to ${label} ${to}`
    return `cleared from ${label} ${from}`
  }
  return `${field}: ${from ?? '—'} → ${to ?? '—'}`
}
